/**
 * Der Schreibweg des Drawers — aus einem ausgefüllten Formular wird ein
 * Datensatz.
 *
 * Bis zum 13.08.2026 hing der Drawer in `main.tsx` **ohne** `onSpeichern`. Alle
 * neun Formulare prüften die Eingaben vollständig und meldeten dann „Noch
 * nicht gespeichert". Das war ehrlich, aber es hieß eben auch: man konnte in
 * diesem Werkzeug nichts anlegen.
 *
 * ## Warum zwischen Formular und Sammlung eine Abbildung steht
 *
 * Die Formulare kommen aus dem Entwurf und tragen dessen Feldnamen — `owner`,
 * `konzept`, `kosten`, `typ`. Die Sammlungen tragen die Spalten der Datenbank
 * — `ownerName`, `berechtigungskonzeptVorhanden`, `lizenzkostenProUserJahr`,
 * `assetTyp`. Beides gleichzusetzen wäre die naheliegende Abkürzung und der
 * sichere Weg in stille Fehler: ein Feld, das die API nicht kennt, wird
 * **abgewiesen**, nicht überlesen — und der Nutzer sähe eine Fehlermeldung
 * über ein Feld, das er nie gesehen hat.
 *
 * Deshalb steht hier je Formular, was wohin geht. Was nicht abgebildet ist,
 * geht nicht mit; das ist ausdrücklich vermerkt, damit niemand annimmt, es
 * sei vergessen worden.
 */

import { senden } from './klient.js';
import { type Aenderungsergebnis } from './rollen.js';
import { tokenHolen } from '../anmeldung/msal.js';

/** Was die Schnittstelle nach dem Anlegen zurückgibt. */
export interface Anlegeergebnis {
  satz: Record<string, unknown>;
  schluessel: string;
  vorgangId: string;
  /**
   * Gesetzt, wenn der Datensatz **nicht** geschrieben, sondern zur Freigabe
   * eingereicht wurde. Die Oberfläche muss beides unterscheiden können:
   * „angelegt" und „liegt jetzt bei jemand anderem" sind verschiedene
   * Auskünfte, und die zweite als die erste auszugeben wäre genau die Art
   * Falschmeldung, die dieses Werkzeug nicht geben darf.
   */
  freigabe?: { eingereicht: true; felder: string[] };
}

/**
 * Ein Formular des Drawers auf eine Sammlung abgebildet.
 *
 * `felder` bildet Formularschlüssel auf Sammlungsfelder ab. `feste` setzt
 * Werte, die das Formular nicht erfragt, weil sie aus seiner Art folgen — ein
 * Laufwerksformular legt immer ein Asset vom Typ „Fileserver" an.
 */
interface Abbildung {
  sammlung: string;
  felder: Record<string, string>;
  feste?: Record<string, unknown>;
  /**
   * Felder, die sich aus den **Eingaben** ergeben.
   *
   * `feste` setzt einen Wert unabhängig von allem; `ableiten` rechnet ihn aus
   * dem aus, was im Formular steht. Zwei Fälle brauchen das, und beide waren
   * bis zum 27.08.2026 falsch:
   *
   * - Das **Postfachformular** setzte hart `assetTyp: Postfach`. Wer darin
   *   „Verteilerliste" wählte, bekam trotzdem ein Postfach — und damit im
   *   Auftragsweg die Zielart `exchange-postfach` statt `exchange-verteiler`.
   *   Die Begründung daneben behauptete, „die Wahl trifft die Ansicht"; es
   *   gibt keine Ansicht, die etwas wählt.
   * - Das **Berechtigungsformular** fragte `assetTyp` gar nicht ab, obwohl
   *   das Feld `nurAnlegen` ist und die Zeile identifiziert. Jede über
   *   `/matrix → + Neu` angelegte Zuordnung bekam `AssetTyp = NULL`, fiel in
   *   `ZIELSYSTEM_SQL` zwangsläufig auf `unbestimmt` und tauchte in keinem
   *   typgefilterten Reiter auf.
   */
  ableiten?: (werte: Record<string, string>) => Record<string, unknown>;
  /** Formularfelder, die bewusst **nicht** mitgehen, mit Begründung. */
  nichtUebernommen?: Record<string, string>;
  /**
   * Formularfelder, die nur beim **Anlegen** geschrieben werden — sie
   * identifizieren den Satz (`nurAnlegen` in `sammlungen.ts`), und die
   * Schnittstelle weist sie beim Aendern ab.
   */
  nurAnlegen?: string[];
}

/** Kategorien, unter denen eine Applikation eine mobile App ist. */
const MOBILE_KATEGORIEN = new Set(['iPhone', 'iPad']);

export const ABBILDUNGEN: Record<string, Abbildung> = {
  applikation: {
    sammlung: 'assets',
    feste: { assetTyp: 'Applikation' },
    /* Kategorie iPhone oder iPad ergibt eine mobile App (07.10.2026, HOWOGE
       Punkt 18). Der Import fuehrt sie als `MobileApp`; was hier neu entsteht,
       soll zum Bestand passen und nicht mit der gleichnamigen
       Desktopanwendung in Zaehlern und Lizenzkosten zusammenfallen. */
    ableiten: (w) => ({ assetTyp: MOBILE_KATEGORIEN.has(w['kategorie'] ?? '') ? 'MobileApp' : 'Applikation' }),
    felder: {
      name: 'name',
      hersteller: 'hersteller',
      kategorie: 'kategorie',
      kritikalitaet: 'kritikalitaet',
      /* `owner` ist der **technische** Owner (`iam.Asset.OwnerName`),
         `ownerFachlich` der fachliche (`FachlicherOwner`). Die Trennung ist
         seit dem 26.08.2026 ausdruecklich; siehe `formulare.ts`. */
      owner: 'ownerName',
      ownerFachlich: 'fachlicherOwner',
      /* Je Verantwortung eine Adresse (`045`, 27.08.2026). Bis dahin wurde
         `ownerMail` erfasst und hier verworfen — es gab keine Spalte. */
      ownerMail: 'ownerMail',
      ownerFachlichMail: 'fachlicherOwnerMail',
      konzept: 'berechtigungskonzeptVorhanden',
      kosten: 'lizenzkostenProUserJahr',
      lizenzhinweis: 'lizenzhinweis',
    },
    nichtUebernommen: {
      typ: 'Der Entwurf meint damit die technische Bereitstellung (Entra App, AVD, …). '
        + 'Das Schema kennt dafür keine Spalte; `assetTyp` ist etwas anderes.',
      lizenzmodell: 'Keine Spalte im Schema. Der Hinweis aus der Preisliste trägt es heute.',
    },
  },

  /* Eine Zeile der Rollenmatrix. Seit dem 27.08.2026 anlegbar; sie geht wie
     jede Aenderung daran durch die Vier-Augen-Freigabe. */
  berechtigung: {
    sammlung: 'zuordnungen',
    feste: {},
    felder: {
      rolleId: 'rolleId',
      asset: 'asset',
      /* `assetTyp` ist `nurAnlegen` und identifiziert die Zeile — ohne ihn
         faellt sie in `ZIELSYSTEM_SQL` auf `unbestimmt` und taucht in keinem
         typgefilterten Reiter auf. Bis zum 27.08.2026 fragte das Formular
         ihn gar nicht ab; jede ueber `/matrix → + Neu` angelegte Zuordnung
         bekam `AssetTyp = NULL`.

         Abgefragt und nicht abgeleitet: ein erster Entwurf rechnete ihn aus
         `kategorie`, so wie `matrix-lesen.py` es aus den Abschnittsnamen der
         Mappe tut. Das traegt nicht — im Bestand steht in `kategorie` die
         **Unterueberschrift** (AVD, Surface, Web-Anwendungen), nicht der
         Abschnitt. Nachgemessen an DEV: dort stehen „Kollaboration",
         „Sicherheit", „iPhone" — keiner davon ist ein Abschnittsname. Eine
         Ableitung, die in der Haelfte der Faelle nichts trifft, ist
         schlimmer als eine Frage. */
      assetTyp: 'assetTyp',
      kategorie: 'kategorie',
      berechtigungsart: 'berechtigungsart',
      gruppenName: 'gruppenName',
      groupId: 'groupId',
      idp: 'idp',
      gruppentyp: 'gruppentyp',
      provisionierung: 'provisionierung',
      status: 'status',
    },
    /* Rolle, Objekt, Objektart und Kategorie stehen nach dem Anlegen fest.
       Bis zum 06.10.2026 gingen sie beim Bearbeiten mit, und die
       Schnittstelle wies die ganze Aenderung ab („Das Feld rolleId kann
       nicht geaendert werden"). */
    nurAnlegen: ['rolleId', 'asset', 'assetTyp', 'kategorie'],
    nichtUebernommen: {
      zusatz: 'Die Spalte iam.Zuordnung.Zusatz gibt es, aber sie trägt eine '
        + 'Bedingung ISJSON = 1 (001), und sammlungen.ts führt sie gar nicht '
        + 'als Feld. Das Semikolonformat dieses Eingabefelds käme dort ohnehin '
        + 'nicht durch. Bis zum 27.08.2026 stand es weder hier noch in felder — '
        + 'eine Eingabe ohne jede Wirkung und ohne Begruendung.',
    },
  },

  laufwerk: {
    sammlung: 'assets',
    /* `kategorie` steht fest und wird nicht mehr erfragt.
     *
     * Bei einem Laufwerk trägt sie im ganzen Bestand denselben Wert — am
     * 28.08.2026 nachgezählt **458 von 458**. Bis dahin war sie ein
     * Pflichtfeld des Formulars („Bezeichnung"), also eine Eingabe ohne
     * Wahl, die sich abweichend ausfüllen ließ und dann eine Kategorie
     * erzeugt hätte, die es sonst nirgends gibt.
     *
     * Nur beim **Anlegen**: `werteFuerAenderung` liest ausschließlich
     * `felder`, ein bestehendes Laufwerk behält damit seine Kategorie. */
    feste: { assetTyp: 'Fileserver', kategorie: 'Laufwerkzugriffe' },
    felder: {
      pfad: 'name',
      owner: 'ownerName',
      /* Je Verantwortung eine Adresse (`045`). Bis zum 01.09.2026 gab es an
         allen Assets ausser der Applikation einen Owner **ohne** Adresse —
         ein Name allein sagt nicht, wen man erreicht. */
      ownerMail: 'ownerMail',
    },
    /* `zugriff` und `gruppenName` stehen hier nicht mehr: sie sind am
       22.09.2026 **aus dem Formular** entfernt worden. Ein Feld, das eine
       Eingabe entgegennimmt und verwirft, ist eine Zusage, die niemand
       einlöst — die Begründung dafür gehört nicht in eine Liste der
       Ausnahmen, sondern an die Stelle, an der das Feld stand
       (`entwurf/formulare.ts`). */
    nichtUebernommen: {
      vererbung: 'Keine Spalte im Schema.',
    },
  },

  postfach: {
    sammlung: 'assets',
    /* Kein festes `assetTyp` mehr — er haengt am gewaehlten Typ. */
    feste: {},
    /* „Verteilerliste" ist ein Verteiler und kein Postfach. Bis zum
       27.08.2026 wurde jeder ueber dieses Formular angelegte Verteiler zum
       Postfach, und der Auftragsweg gab ihm die falsche Zielart. */
    ableiten: (w) => ({
      assetTyp: w['typ'] === 'Verteilerliste' ? 'Verteiler' : 'Postfach',
    }),
    felder: {
      adresse: 'name',
      anzeigename: 'kategorie',
      owner: 'ownerName',
      /* Je Verantwortung eine Adresse (`045`). Bis zum 01.09.2026 gab es an
         allen Assets ausser der Applikation einen Owner **ohne** Adresse —
         ein Name allein sagt nicht, wen man erreicht. */
      ownerMail: 'ownerMail',
    },
    nichtUebernommen: {
      typ: 'Wird nicht als eigene Spalte gespeichert: die Unterscheidung, auf die '
        + 'es ankommt, trägt `assetTyp` — und der wird seit dem 27.08.2026 aus '
        + 'genau diesem Feld abgeleitet (siehe `ableiten`). Die feineren Werte '
        + '(Raum, Ressource, dynamisch) kennt das Schema nicht.',
      /* `zugriffsart` ist am 22.09.2026 aus dem Formular entfernt worden und
         steht jetzt an der Zuordnung — siehe `entwurf/formulare.ts`. */
      aufbewahrung: 'Keine Spalte im Schema.',
      extern: 'Keine Spalte im Schema.',
    },
  },

  team: {
    sammlung: 'assets',
    feste: { assetTyp: 'M365' },
    felder: {
      name: 'name',
      typ: 'kategorie',
      /* **Ein** Owner. Bis zum 01.09.2026 schrieb dieses Formular als
         einziges beide Spalten; der Kunde hat an Teams und M365-Gruppen
         genau eine Verantwortung. `FachlicherOwner` steht weiter im
         Bestand und wird nur nicht mehr geschrieben — was dort steht,
         bleibt stehen. */
      owner: 'ownerName',
      ownerMail: 'ownerMail',
      /* Seit dem 27.08.2026. Das Feld hiess hier `vertraulichkeit`, hatte
         keine Spalte und wurde verworfen — waehrend die Liste daneben
         `Kritikalitaet` unter der Ueberschrift „Vertraulichkeit" zeigte. */
      kritikalitaet: 'kritikalitaet',
    },
    nichtUebernommen: {
      url: 'Keine Spalte im Schema.',
      gaeste: 'Keine Spalte im Schema.',
    },
  },

  hardware: {
    sammlung: 'assets',
    feste: { assetTyp: 'Hardware' },
    felder: {
      geraeteklasse: 'kategorie',
      bezeichnung: 'name',
      kosten: 'lizenzkostenProUserJahr',
      begruendung: 'lizenzhinweis',
      /* `iam.Asset.Verweis` aus `034`. Fehlte hier bis zum 25.08.2026 — und
         weil `vorbelegungAusSatz` genau ueber diese Abbildung laeuft, wurde
         der Warenkorblink beim Bearbeiten weder vorbelegt noch
         zurueckgeschrieben. Ein Feld, das ein Formular still verwirft, ist
         schlimmer als eines, das fehlt. */
      verweis: 'verweis',
      /* Je Verantwortung eine Adresse (`045`). Bis zum 01.09.2026 gab es an
         allen Assets ausser der Applikation einen Owner **ohne** Adresse —
         ein Name allein sagt nicht, wen man erreicht. */
      owner: 'ownerName',
      ownerMail: 'ownerMail',
    },
    nichtUebernommen: {
      standard: 'Keine Spalte im Schema.',
    },
  },

  /* Mobile Anwendungen sind eine eigene Objektart — vorher schrieb ihr Reiter
     ueber `hardware` und damit `assetTyp: 'Hardware'`. */
  mobileapp: {
    sammlung: 'assets',
    feste: { assetTyp: 'MobileApp' },
    felder: {
      bezeichnung: 'name',
      kategorie: 'kategorie',
      kosten: 'lizenzkostenProUserJahr',
      verweis: 'verweis',
      /* Je Verantwortung eine Adresse (`045`). Bis zum 01.09.2026 gab es an
         allen Assets ausser der Applikation einen Owner **ohne** Adresse —
         ein Name allein sagt nicht, wen man erreicht. */
      owner: 'ownerName',
      ownerMail: 'ownerMail',
    },
  },

  gruppe: {
    sammlung: 'gruppen',
    felder: {
      name: 'name',
      groupId: 'groupId',
      idp: 'idp',
      gruppentyp: 'gruppentyp',
      zielsystem: 'zielsystem',
      owner: 'owner',
    },
    nichtUebernommen: {
      beschreibung: 'Keine Spalte in `iam.Gruppe`.',
    },
  },

  punkt: {
    sammlung: 'offene-punkte',
    felder: {
      thema: 'thema',
      oeCode: 'oeCode',
      status: 'status',
      verantwortlich: 'verantwortlich',
      faelligkeit: 'faelligkeit',
      bemerkung: 'bemerkung',
      /* Bis zum 27.08.2026 fehlten diese beiden hier.

         Das Formular erfasste sie (`formulare.ts`, Gruppe „Herkunft"), die
         Sammlung fuehrte sie als schreibbar, die Weissliste des
         Entwicklungsservers kannte sie — und `werteBauen` laeuft ueber
         **diese** Liste. Beide Eingaben wurden also still verworfen, und die
         Anwendung meldete Erfolg.

         Damit war die Anforderung vom 25.08.2026 („wer ihn angefordert hat
         und unter welchem Ticket", Migration `039`) ueber die Oberflaeche
         nicht erfuellt: Schema, Weissliste und Formular standen, nur die
         Bruecke fehlte. Genau der Fall, vor dem der Dateikopf oben warnt. */
      angefordertVon: 'angefordertVon',
      ticket: 'ticket',
    },
  },
};

/**
 * Formulare ohne Abbildung — mit dem Grund, warum sie keine haben.
 *
 * Sie stehen hier, damit die Lücke benannt ist und nicht als Versehen
 * erscheint. Der Drawer meldet für sie weiterhin, dass nichts gespeichert
 * wurde — das ist die richtige Auskunft, solange der Weg fehlt.
 */
export const OHNE_WEG: Record<string, string> = {
};

/**
 * Aus einem Datensatz die Vorbelegung eines Formulars machen — die
 * **Umkehrung** von `werteBauen`.
 *
 * Die Formulare stammen aus dem Entwurf und tragen eigene Feldnamen: das
 * Laufwerksformular fragt nach `pfad` und `bezeichnung`, die Sammlung fuehrt
 * `name` und `kategorie`. `ABBILDUNGEN` uebersetzt das beim **Speichern**
 * laengst — beim **Oeffnen** tat es das bis zum 14.08.2026 niemand.
 *
 * Wirkung: „Bearbeiten" an einem Laufwerk oder Postfach oeffnete ein leeres
 * Formular, das aussah wie ein Anlegeformular. Der Datensatz reichte seine
 * Werte unter `name` und `kategorie` herein, das Formular suchte `pfad` und
 * `bezeichnung`, und keiner der beiden fand den anderen.
 *
 * Felder, die die Sammlung nicht fuehrt, bleiben leer — sie stehen in
 * `nichtUebernommen` und haben dort ihren Grund.
 */
export function vorbelegungAusSatz(
  formular: string,
  satz: Record<string, unknown>,
): Record<string, string> {
  const abbildung = Object.hasOwn(ABBILDUNGEN, formular) ? ABBILDUNGEN[formular] : undefined;
  if (!abbildung) return {};
  const werte: Record<string, string> = {};
  for (const [formularfeld, sammlungsfeld] of Object.entries(abbildung.felder)) {
    const wert = satz[sammlungsfeld];
    if (wert === null || wert === undefined || wert === '') continue;
    /* Wahrheitswerte als `ja`/`nein`: die Formulare fuehren sie als Auswahl,
       und `String(true)` waere „true" und traefe keine Option. */
    werte[formularfeld] = typeof wert === 'boolean'
      ? (wert ? 'ja' : 'nein')
      : deutschesDatum(String(wert));
  }
  return werte;
}

/**
 * Ein Datum aus der Schnittstelle in die Schreibweise des Formulars.
 *
 * Die Sammlungen liefern Daten als `2026-07-12`; die Formulare verlangen
 * `TT.MM.JJJJ` und prüfen das mit einem Muster. Ohne diese Umschrift stand im
 * Feld ein Wert, den das eigene Formular als ungültig zurückwies — gemessen
 * am 18.08.2026 beim ersten Bearbeiten eines Klärungspunkts: „Fälligkeit
 * 2026-07-12", Speichern gesperrt, und der einzige Ausweg war, das Datum von
 * Hand neu zu tippen.
 *
 * Was nicht wie ein ISO-Datum aussieht, bleibt unangetastet. Eine Umschrift,
 * die rät, macht aus einem lesbaren Wert einen falschen.
 */
function deutschesDatum(wert: string): string {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(wert);
  if (!treffer) return wert;
  return `${treffer[3]}.${treffer[2]}.${treffer[1]}`;
}

/**
 * Ein Datum aus der Schreibweise des Formulars in die der Schnittstelle.
 *
 * Gegenstück zu `deutschesDatum`. Die Formulare verlangen `TT.MM.JJJJ` — so
 * schreibt man hier ein Datum —, die Schnittstelle nimmt seit dem 18.08.2026
 * ausschließlich `JJJJ-MM-TT` und weist alles andere ab.
 *
 * **Warum sie es abweist, gehört hierher:** vorher deutete der Server die
 * Eingabe mit `new Date(...)`, und der liest `12.07.2026` als 6. Dezember.
 * Ein Tag bis zum zwölften wurde also still zum Monat, ab dem dreizehnten
 * scheiterte es. Ein falsch gespeichertes Fälligkeitsdatum fällt erst auf,
 * wenn jemand die Frist verpasst hat.
 */
function isoDatum(wert: string): string {
  const treffer = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(wert);
  if (!treffer) return wert;
  return `${treffer[3]}-${treffer[2]}-${treffer[1]}`;
}

/**
 * Felder der Sammlungen, die eine **Zahl** fuehren.
 *
 * Abgeleitet aus `apps/api/src/sammlungen.ts` (dort `z(...)`); zusammengehalten
 * von `werkzeuge/test/anlegen-weissliste.test.mjs`, damit die Liste nicht
 * auseinanderlaeuft.
 */
const ZAHLFELDER = new Set([
  'lizenzkostenProUserJahr', 'anzahlNutzer', 'kosten', 'nummer',
]);

/**
 * Deutsche Zahlen in das Format, das die Schnittstelle versteht.
 *
 * Der Kunde hat es am 27.08.2026 gemeldet: das Feld
 * „Netto-Lizenzkosten je Nutzer und Jahr" schlaegt `134,23` als Beispiel vor,
 * das Muster im Formular laesst das Komma zu — und der Server antwortete
 * „Zahl erwartet". Nur `132.23` ging durch.
 *
 * Der Fehler lag dazwischen: das Formular gab die Zeichenkette weiter, wie
 * sie eingetippt war. Eine deutsche Oberflaeche, die ein deutsches Komma
 * vorschlaegt und es dann abweist, ist an der Stelle falsch, an der sie es
 * weiterreicht — nicht an der, an der sie es annimmt.
 *
 * Umgestellt wird **nur**, was auch ein Zahlfeld ist. Ein Komma in einem
 * Namen bleibt ein Komma.
 */
function zahlwert(wert: string): string {
  return /^[0-9]{1,9}[.,][0-9]{1,2}$/.test(wert) ? wert.replace(',', '.') : wert;
}

/** Leere Zeichenketten fallen weg — ein leeres Feld ist keine Angabe. */
function werteBauen(abbildung: Abbildung, werte: Record<string, string>) {
  const rumpf: Record<string, unknown> = {
    ...(abbildung.feste ?? {}),
    /* Abgeleitetes steht **vor** den Formularfeldern: es entsteht aus ihnen,
       und eine Abbildung, die dasselbe Feld noch einmal setzt, soll gewinnen.
       Heute tut das keine — die Reihenfolge sagt trotzdem, welche gälte. */
    ...(abbildung.ableiten?.(werte) ?? {}),
  };
  for (const [formularfeld, sammlungsfeld] of Object.entries(abbildung.felder)) {
    /* Namen ohne Tabulatoren und doppelte Leerzeichen — wer aus Excel
       einfuegt, bringt sie mit, und „gleich aussehend" hiesse sonst zwei
       Assets (30.09.2026; dieselbe Regel wie `matrix-lesen.py`). */
    const roh = (werte[formularfeld] ?? '').trim();
    const wert = sammlungsfeld === 'name' ? roh.replace(/\s+/g, ' ') : roh;
    if (wert === '') continue;
    rumpf[sammlungsfeld] = ZAHLFELDER.has(sammlungsfeld)
      ? zahlwert(wert)
      : isoDatum(wert);
  }
  return rumpf;
}

/**
 * Legt aus einem ausgefüllten Formular einen Datensatz an.
 *
 * Wirft, wenn es für das Formular keinen Weg gibt — der Aufrufer meldet das
 * dann als das, was es ist: eine fehlende Anbindung, kein Eingabefehler.
 */
export async function ausFormularAnlegen(
  mandant: string,
  formular: string,
  werte: Record<string, string>,
  begruendung?: string,
  /**
   * Klammert das Anlegen mit weiteren Schritten zu **einem** Vorgang — etwa
   * mit den Rollen, die im selben Formular gewählt wurden, oder mit dem
   * Ersetzen, das auf das neue Asset überträgt. Dasselbe Muster wie
   * `rolleAnlegen` mit seinen Positionen.
   */
  vorgangId?: string,
): Promise<Anlegeergebnis> {
  const abbildung = ABBILDUNGEN[formular];
  if (!abbildung) {
    throw new Error(
      OHNE_WEG[formular]
      ?? `Für das Formular „${formular}" ist kein Schreibweg hinterlegt.`,
    );
  }
  return senden<Anlegeergebnis>(`/api/${abbildung.sammlung}`, 'POST', {
    mandant,
    token: await tokenHolen(),
    rumpf: { werte: werteBauen(abbildung, werte), begruendung, ...(vorgangId ? { vorgangId } : {}) },
  });
}

/**
 * Was ein Formular schreibt, wenn es einen **bestehenden** Satz ändert.
 *
 * Drei Unterschiede zu `werteBauen`, und jeder einzelne war ein Fehler:
 *
 * 1. **Ein leeres Feld heißt „löschen".** `werteBauen` überspringt Leerwerte —
 *    beim Anlegen richtig, denn was nicht ausgefüllt wurde, soll die Vorgabe
 *    der Spalte bekommen. Beim Ändern ist es das Gegenteil: wer den
 *    Warenkorb-Link aus dem Feld entfernt und speichert, **will** ihn los
 *    sein. Übersprungen hieße, dass Löschen unmöglich ist — genau so hat der
 *    Kunde es am 28.08.2026 gemeldet.
 * 2. **`feste` bleibt draußen.** Das sind Werte wie `assetTyp`, die den Satz
 *    identifizieren. Sie tragen `nurAnlegen`, und die Schnittstelle weist sie
 *    beim Ändern zu Recht ab.
 * 3. **`ableiten` bleibt draußen**, aus demselben Grund: es erzeugt
 *    identifizierende Werte aus den Eingaben.
 *
 * Geschrieben werden also ausschließlich die Felder, die das Formular selbst
 * führt — was es nicht zeigt, fasst es auch nicht an.
 */
function werteFuerAenderung(abbildung: Abbildung, werte: Record<string, string>) {
  const rumpf: Record<string, unknown> = {};
  for (const [formularfeld, sammlungsfeld] of Object.entries(abbildung.felder)) {
    if (abbildung.nurAnlegen?.includes(formularfeld)) continue;
    /* `undefined` heißt „das Formular hat dieses Feld gar nicht gezeigt" und
       ist etwas anderes als der leere Text, den jemand ausdrücklich stehen
       gelassen hat. Nur das Zweite wird geschrieben. */
    const roh = werte[formularfeld];
    if (roh === undefined) continue;
    const wert = roh.trim();
    if (wert === '') { rumpf[sammlungsfeld] = null; continue; }
    rumpf[sammlungsfeld] = ZAHLFELDER.has(sammlungsfeld) ? zahlwert(wert) : isoDatum(wert);
  }
  return rumpf;
}

/**
 * Schreibt ein ausgefülltes Formular auf einen **bestehenden** Satz.
 *
 * ## Warum es diese Funktion gibt
 *
 * Bis zum 28.08.2026 gab es sie nicht. Der Drawer kennt seit jeher einen
 * Bearbeitungsmodus und reicht ihn als dritten Parameter an `onSpeichern`
 * durch — `Anlegeweg.tsx` nahm die Rückmeldung aber als `(schluessel, werte)`
 * entgegen und **verwarf den dritten Parameter**. Jeder Klick auf
 * „Änderungen speichern" rief deshalb `ausFormularAnlegen`.
 *
 * Die Wirkung ist die schlimmste Sorte: nichts scheitert, nichts meldet sich,
 * und der Bestand hat danach eine Zeile mehr statt eine geänderte. Der Kunde
 * hat es am Warenkorb gemerkt — betroffen war **jedes** Feld jedes Assets,
 * das über den Drawer bearbeitet wurde.
 *
 * `if-match` trägt die Zeilenversion: ohne sie ließe sich nicht sagen, ob
 * inzwischen jemand anders geschrieben hat, und der Konfliktweg hätte nichts
 * zu vergleichen.
 */
export async function ausFormularAendern(
  mandant: string,
  formular: string,
  schluessel: string,
  version: string,
  werte: Record<string, string>,
  begruendung?: string,
): Promise<Aenderungsergebnis> {
  const abbildung = ABBILDUNGEN[formular];
  if (!abbildung) {
    throw new Error(
      OHNE_WEG[formular]
      ?? `Für das Formular „${formular}" ist kein Schreibweg hinterlegt.`,
    );
  }
  return senden<Aenderungsergebnis>(
    `/api/${abbildung.sammlung}/${encodeURIComponent(schluessel)}`,
    'PATCH',
    {
      mandant,
      token: await tokenHolen(),
      rumpf: { aenderungen: werteFuerAenderung(abbildung, werte), begruendung },
      zusatzkopf: { 'if-match': version },
    },
  );
}

/**
 * Die Objektart, die ein Formular mit diesen Werten **anlegt** — `ableiten`
 * vor `feste`, wie in `werteBauen`.
 *
 * Gebraucht von der Rollenzuordnung am Asset: die Zuordnungen haengen an
 * Name **und** Objektart, und beim Anlegen steht die Objektart nur im
 * Formular. Beim Bearbeiten gilt die gespeicherte Objektart des Satzes,
 * nicht diese Rechnung (`assetTyp` ist `nurAnlegen`).
 */
export function assetTypAusFormular(formular: string, werte: Record<string, string>): string | null {
  const abbildung = Object.hasOwn(ABBILDUNGEN, formular) ? ABBILDUNGEN[formular] : undefined;
  if (!abbildung || abbildung.sammlung !== 'assets') return null;
  const abgeleitet = abbildung.ableiten?.(werte)['assetTyp'];
  if (typeof abgeleitet === 'string' && abgeleitet) return abgeleitet;
  const fest = abbildung.feste?.['assetTyp'];
  return typeof fest === 'string' && fest ? fest : null;
}

/**
 * Die Kategorie, die ein Asset-Formular mit diesen Werten traegt — aus dem
 * Feld, das auf `kategorie` abbildet, sonst aus `feste` (Laufwerke). Seit dem
 * 07.10.2026 Teil der Identitaet: AVD- und Surface-Fassung einer Anwendung
 * sind zwei Applikationen gleichen Namens.
 */
export function kategorieAusFormular(formular: string, werte: Record<string, string>): string {
  const abbildung = Object.hasOwn(ABBILDUNGEN, formular) ? ABBILDUNGEN[formular] : undefined;
  if (!abbildung) return '';
  const feld = Object.entries(abbildung.felder).find(([, s]) => s === 'kategorie')?.[0];
  if (feld) return (werte[feld] ?? '').trim();
  const fest = abbildung.feste?.['kategorie'];
  return typeof fest === 'string' ? fest : '';
}

/** Das Formularfeld, das den Namen eines Assets traegt (`name`, `pfad`, `adresse` …). */
export function namensfeldAusFormular(formular: string): string | null {
  const abbildung = Object.hasOwn(ABBILDUNGEN, formular) ? ABBILDUNGEN[formular] : undefined;
  if (!abbildung) return null;
  return Object.entries(abbildung.felder).find(([, s]) => s === 'name')?.[0] ?? null;
}

/**
 * Wohin die Ansicht nach dem Anlegen springen soll.
 *
 * Am **Formular** festgemacht und nicht mehr an `feste.assetTyp`. Seit das
 * Postfachformular seinen Typ ableitet, steht dort keiner mehr — und die
 * Ansicht wäre stehengeblieben, ohne dass jemand wüsste warum. Ein Ziel, das
 * an einem Feld hängt, das aus einem anderen Grund verschwinden kann, ist
 * kein Ziel.
 *
 * Ein Formular ohne Eintrag springt nirgendwohin. Das ist der richtige
 * Vorgabewert: die Rollenmatrix etwa bleibt stehen, wo sie ist.
 */
const ZIELROUTE: Record<string, string> = {
  applikation: '/assets/applikationen',
  laufwerk: '/assets/fileserver',
  /* Postfach **und** Verteiler stehen im selben Reiter. */
  postfach: '/assets/exchange',
  team: '/assets/m365',
  hardware: '/assets/hardware',
  mobileapp: '/assets/applikationen',
  gruppe: '/assets/gruppen',
  punkt: '/offene-punkte',
};

export function zielRoute(formular: string): string | null {
  if (!ABBILDUNGEN[formular]) return null;
  return ZIELROUTE[formular] ?? null;
}
